import { matchPath } from 'react-router';
import { call, put, fork, takeLatest, select, take, actionChannel } from 'redux-saga/effects';

import { updateModelFailure, updateModelSuccess } from 'actions/modelActions';
import { restClient } from 'config/store/configureStore';
import * as types from 'constants/ActionTypes';
import { MAX_MODEL_UPLOAD_SIZE } from 'constants/MediaConstants';

import { PATH } from 'constants/RoutesConstants';
import { SELECT_GROUP } from 'modules/groups/actionTypes';

import { createNotification } from 'modules/toast-notifications/actions';

import { hasFilesToUpload, getModelsTableParameters, getModelsSelectLinks } from 'selectors/modelSelectors';
import { getCurrentLocation } from 'selectors/router';

import * as modelActions from '../actions/modelActions';

import ModelService from '../services/api/ModelService';

// GET SPECIFIC MODEL
function* fetchModel({ payload }) {
  const { request } = payload;
  try {
    const response = yield call(restClient.request, request);
    yield put(modelActions.fetchModelSuccess(response.data));
  } catch (e) {
    yield put(modelActions.fetchModelFailure(e));
  }
}

function* fetchModelWatcher() {
  yield takeLatest(types.FETCH_MODEL_REQUEST, fetchModel);
}

// GET ALL MODELS
function* fetchModels(action) {
  try {
    const response = yield call(ModelService.getModels, action.links, action.params);
    yield put(modelActions.fetchModelsSuccess(response.data.content, response.data.page, response.data.links));
  } catch (e) {
    yield put(modelActions.fetchModelsFailure(e));
    yield put(
      createNotification({
        type: 'error',
        message: {
          id: 'http.error.message',
          defaultMessage: 'Oops! Something went wrong.',
        },
      })
    );
  }
}

function* fetchModelsWatcher() {
  yield takeLatest(types.FETCH_MODELS_REQUEST, fetchModels);
}

// GET MODELS FOR SELECT
function* fetchModelsSelect({ payload }) {
  const { request } = payload;
  try {
    const response = yield call(restClient.request, request);
    yield put(modelActions.fetchModelsSelectSuccess(response.data));
  } catch (e) {
    yield put(modelActions.fetchModelsSelectFailure(e));
  }
}

function* fetchModelsSelectWatcher() {
  yield takeLatest(types.FETCH_MODELS_SELECT_REQUEST, fetchModelsSelect);
}

function* fetchModelsSelectNextPage() {
  const links = yield select(getModelsSelectLinks);
  const request = {
    method: 'GET',
    url: links.next.href,
  };
  try {
    const response = yield call(restClient.request, request);
    yield put(modelActions.fetchModelsSelectSuccess(response.data));
  } catch (e) {
    yield put(modelActions.fetchModelsSelectFailure(e));
  }
}

function* fetchModelsSelectNextPageWatcher() {
  yield takeLatest(types.FETCH_MODELS_SELECT_NEXT_PAGE_REQUEST, fetchModelsSelectNextPage);
}

// GET SPECIFIC MODEL FOR SELECT

function* uploadModelsWatcher() {
  const channel = yield actionChannel(types.UPLOAD_MODELS_REQUEST);
  while (true) {
    const action = yield take(channel);
    const { models } = action.payload;
    try {
      if (models.size > MAX_MODEL_UPLOAD_SIZE) {
        throw new Error(`The size limit to upload a model is ${MAX_MODEL_UPLOAD_SIZE / 1000000} mb`);
      }
      yield put(modelActions.uploadModelRequest(models.name));
      yield call(ModelService.uploadModels, models);
      yield put(modelActions.uploadModelsSuccess());
      const hasFiles = yield select(hasFilesToUpload);
      const params = yield select(getModelsTableParameters);
      if (!hasFiles) {
        yield put(modelActions.fetchModels(null, params));
      }
    } catch (e) {
      yield put(modelActions.uploadModelsFailure(e));
      yield put(
        createNotification({
          type: 'error',
          message: {
            id: 'page.configurator.models.notification.update.error',
            defaultMessage: 'An error occurred while uploading the model {fileName}',
            values: { fileName: models.name },
          },
          duration: 0,
        })
      );
    }
  }
}

// UPDATE MODEL
function* updateModelWorker({ payload }) {
  try {
    const response = yield call(restClient.request, payload.request);
    yield put(updateModelSuccess(response.data));
    yield put(
      createNotification({
        type: 'success',
        message: {
          id: 'page.configurator.models.notification.update.success',
          defaultMessage: 'Model saved',
        },
      })
    );
  } catch (error) {
    yield put(updateModelFailure({ error }));
    yield put(
      createNotification({
        type: 'error',
        message: {
          id: 'page.configurator.models.notification.update.error',
          defaultMessage: 'An error occurred while uploading the models',
        },
      })
    );
  }
}

function* updateModelWatcher() {
  yield takeLatest(types.UPDATE_MODEL_REQUEST, updateModelWorker);
}

// TOGGLE BETWEEN VALID/INVALID MODELS
function* toggleValidModels() {
  const location = yield select(getCurrentLocation);

  if (matchPath(PATH.CONFIGURATOR_MODELS, location)) {
    const params = yield select(getModelsTableParameters);
    yield put(modelActions.fetchModels(null, { ...params, page: 0 }));
  }
}

function* toggleValidModelsWatcher() {
  yield takeLatest([types.TOGGLE_VALID_MODELS, SELECT_GROUP], toggleValidModels);
}

const ModelSagas = [
  fork(fetchModelWatcher),
  fork(fetchModelsWatcher),
  fork(fetchModelsSelectWatcher),
  fork(uploadModelsWatcher),
  fork(updateModelWatcher),
  fork(toggleValidModelsWatcher),
  fork(fetchModelsSelectNextPageWatcher),
];

export default ModelSagas;
